import React, { memo, useState, useEffect } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import makeStyles from "@material-ui/core/styles/makeStyles";
import useTheme from "@material-ui/core/styles/useTheme";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Selector from "~styled/selector/Selector";
import {
  motivoAnulacionFilter,
  motivoAnulacionOptions,
} from "~constants/endosos";
import pxToRem from "~libs/pxToRem";
import DatePicker from "~styled/datePicker/DatePicker";

const useStyles = makeStyles((theme: any) => ({
  root: { display: "flex", height: "100%" },
  compania: {
    textAlign: "center",
    fontWeight: 900,
    color: theme.colours.violeta,
    paddingTop: 15,
    paddingBottom: 8,
    display: "block",
    flex: 1,
  },
  tipoPoliza: {
    textAlign: "center",
    fontWeight: 900,
    color: "#afafaf",
    textTransform: "uppercase",
  },
  polizaTitle: { fontSize: pxToRem(12) },
  importe: {
    textAlign: "center",
    fontWeight: 800,
    color: theme.colours.violeta,
  },
  content: { paddingBottom: 50 },
  sentenceTitle: {
    textAlign: "center",
    fontWeight: 900,
    color: "#afafaf",
    textTransform: "uppercase",
    paddingTop: "2vh",
    paddingBottom: "2vh",
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-around",
    padding: "5%",
    alignItems: "center",
    maxWidth: 650,
    width: "100%",
  },
  buttonBack: {
    width: "40vw",
    maxWidth: 200,
    marginTop: 20,
    background: theme.colours.white,
    color: "#455597",
    borderRadius: 50,
    border: 0,
    boxShadow: "2px 2px 10px 0 #CCCCE4",
    height: 47,
    fontWeight: 900,
    fontSize: 16,
    padding: "0 20px",
    textTransform: "none",
    fontStyle: "normal",
    fontStretch: "normal",
    letterSpacing: "0.96px",
    "&:disabled": { background: "#616161", color: "#C4C4C4" },
  },
  button: {
    width: "40vw",
    maxWidth: 200,
    marginTop: 20,
    background: theme.colours.verdeMgBroker,
    borderRadius: 50,
    border: 0,
    color: theme.colours.white,
    boxShadow: "2px 2px 10px 0 #CCCCE4",
    height: 47,
    fontWeight: 900,
    fontSize: 16,
    padding: "0 20px",
    textTransform: "none",
    fontStyle: "normal",
    fontStretch: "normal",
    letterSpacing: "0.96px",
    "&:disabled": { background: "#616161", color: "#C4C4C4" },
  },
  formControl: { margin: theme.spacing(1), minWidth: 120 },
  motivoSelector: { position: "absolute", width: "90%" },
}));

type V = string | number;

export interface PopupAnularProps {
  isOpened: boolean;
  onClose: () => void;
  polizaId: string | number;
  compania: string;
  nroPoliza: string | number;
  titular: string;
  onClickAceptar: (
    motivo: V,
    polizaId: string | number,
    fechaAnulacionISO: string
  ) => void;
}

const LocalDatePicker = DatePicker as unknown as React.ComponentType<{
  onChange: (d: Date | null) => void;
  value: Date | null;
  fullWidth?: boolean;
}>;

function PopupAnularBase({
  isOpened,
  onClose,
  polizaId,
  compania,
  nroPoliza,
  titular,
  onClickAceptar,
}: PopupAnularProps): JSX.Element {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [motivoAnulacion, setMotivoAnulacion] = useState<V | null>(null);
  const [fechaAnulacion, setFechaAnulacion] = useState<Date | null>(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    setEnabled(
      motivoAnulacion !== null &&
        motivoAnulacion !== "" &&
        fechaAnulacion !== null
    );
  }, [motivoAnulacion, fechaAnulacion]);

  const handleChangeMotivoAnulacion = (motivo: V) => setMotivoAnulacion(motivo);
  const handleFechaAnulacion = (fecha: Date | null) => setFechaAnulacion(fecha);

  return (
    <div className={classes.root}>
      <Dialog
        open={isOpened}
        scroll="paper"
        onClose={onClose}
        fullScreen={fullScreen}
      >
        <DialogTitle style={{ padding: 0 }} disableTypography>
          <div style={{ display: "flex", height: "100%" }}>
            <div style={{ width: 48 }} />
            <Typography variant="h5" className={classes.compania}>
              Anular póliza
            </Typography>
            <div style={{ width: 48 }}>
              <IconButton
                edge="end"
                color="inherit"
                onClick={onClose}
                aria-label="Close"
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
          <Typography variant="subtitle2" className={classes.tipoPoliza}>
            <span className={classes.polizaTitle}>Compañía:</span> {compania}
          </Typography>
          <Typography variant="subtitle2" className={classes.tipoPoliza}>
            <span className={classes.polizaTitle}>Número de Póliza:</span>{" "}
            {nroPoliza}
          </Typography>
          <Typography variant="subtitle2" className={classes.tipoPoliza}>
            <span className={classes.polizaTitle}>Titular:</span> {titular}
          </Typography>
        </DialogTitle>

        <DialogContent className={classes.content}>
          <Divider />
          <Typography variant="subtitle2" className={classes.sentenceTitle}>
            Motivo
          </Typography>
          <div className={classes.motivoSelector}>
            <Selector
              id={"motivo"}
              value={motivoAnulacionOptions[motivoAnulacion]}
              options={motivoAnulacionFilter}
              onChange={handleChangeMotivoAnulacion}
              variant={"big"}
            />
          </div>
        </DialogContent>

        <DialogContent className={classes.content}>
          <Typography variant="subtitle2" className={classes.sentenceTitle}>
            Fecha de Anulación
          </Typography>
          <div>
            <LocalDatePicker
              onChange={handleFechaAnulacion}
              value={fechaAnulacion}
              fullWidth
            />
          </div>
        </DialogContent>

        <div className={classes.buttonContainer}>
          <Button
            onClick={onClose}
            className={classes.buttonBack}
            variant="contained"
          >
            CANCELAR
          </Button>
          <Button
            className={classes.button}
            onClick={() =>
              onClickAceptar(
                motivoAnulacion as V,
                polizaId,
                (fechaAnulacion as Date).toISOString()
              )
            }
            variant="contained"
            color="primary"
            disabled={!enabled}
          >
            ACEPTAR
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

export default memo<PopupAnularProps>(PopupAnularBase);
